import { create } from 'zustand';
import type { AppState, AxisSelection, GuestPoint, SliceConfig, GuestCoordinates } from '../types/pca';
import { loadPCAData } from '../utils/loadData';
import { calculateMinMax, getColorForGuest, getColorForCluster, getPointSize } from '../utils/colorMapping';
import { getGuestPopularity } from '../utils/episodePopularity';

type DataMode = 'all' | 'guest_only' | 'enriched' | 'keywords';
type ColorMode = 'cluster' | 'position';

interface PCAStore extends AppState {
  // Color mode state
  colorMode: ColorMode;
  // Size mode state
  sizeMode: 'fixed' | 'popularity'; // Whether to scale points by popularity
  // Cluster selection state
  selectedCluster: number | null; // When set, highlights all guests in this cluster
  // Comparison state
  comparisonGuest: string | null;
  showComparisonLine: boolean; // Only show line when "Find Most Different" is clicked
  comparisonMode: 'idle' | 'waiting-first' | 'waiting-second'; // State for Compare PMs feature
  comparisonFirstGuest: string | null; // First PM selected in comparison mode
  resetTrigger: number; // Increment to trigger camera reset
  // Quiz state
  userCoordinates: number[] | null; // User's 20D coordinates from quiz
  userAnswers: Record<string, number>; // Quiz answers by question ID
  isQuizActive: boolean; // Whether quiz modal is open
  // Actions
  loadData: (mode: DataMode) => Promise<void>;
  setAxisSelection: (axis: Partial<AxisSelection>) => void;
  setSelectedGuest: (guest: string | null) => void;
  setHoveredGuest: (guest: string | null) => void;
  setComparisonGuest: (guest: string | null) => void;
  setMostDifferentComparison: (guest: string | null) => void; // "Find Most Different" - shows line, clears Compare PMs state
  setCompareComparison: (firstGuest: string, secondGuest: string) => void; // "Compare PMs" - shows line, keeps both guests
  setComparisonMode: (mode: 'idle' | 'waiting-first' | 'waiting-second') => void;
  setComparisonFirstGuest: (guest: string | null) => void;
  // Quiz actions
  setQuizActive: (active: boolean) => void;
  setUserAnswer: (questionId: string, value: number) => void;
  setUserCoordinates: (coordinates: number[]) => void;
  clearUserData: () => void;
  setMode: (mode: DataMode) => Promise<void>;
  setViewMode: (mode: '3d' | '2d') => void;
  setColorMode: (mode: ColorMode) => void;
  setSizeMode: (mode: 'fixed' | 'popularity') => void;
  setSelectedCluster: (cluster: number | null) => void;
  setCameraPosition: (position: [number, number, number]) => void;
  setCameraTarget: (target: [number, number, number]) => void;
  resetCamera: () => void;
  resetView: () => void; // Reset camera and axes
  getGuestPoints: () => GuestPoint[];
  clearCache: () => void;
  // Slice actions
  addSlice: (dimension: number) => void;
  updateSlice: (id: string, config: Partial<Omit<SliceConfig, 'id'>>) => void;
  removeSlice: (id: string) => void;
  clearSlices: () => void;
  getDimensionRange: (dimension: number) => { min: number; max: number };
  getGuestByName: (name: string) => GuestCoordinates | null;
  getGuestCluster: (name: string) => number | undefined;
  getDimensionName: (dimension: number) => string;
  getDimensionLabels: (dimension: number) => { low: string; high: string };
  getTotalDimensions: () => number;
  getUniqueClusters: () => number[];
  // User point
  getUserPoint: () => GuestPoint | null; // Get user's 3D point if coordinates exist
  // Distance calculations
  getDistanceBetween: (guest1: string, guest2: string) => number | null;
  getMostDifferentGuest: (guest: string) => { name: string; distance: number } | null;
  getGuestDifferences: (guest1: string, guest2: string) => {
    distance: number;
    topDifferences: Array<{ dimension: number; name: string; diff: number; guest1Value: number; guest2Value: number }>;
  } | null;
  getAllGuestDifferences: (guest1: string, guest2: string) => {
    distance: number;
    allDifferences: Array<{ dimension: number; name: string; diff: number; guest1Value: number; guest2Value: number }>;
  } | null;
  // Relative distance scoring (0-100 percentile)
  getDistancePercentile: (distance: number) => number;
  getDistanceStats: () => { min: number; max: number; median: number; percentiles: number[] } | null;
}

const defaultAxisSelection: AxisSelection = { x: 1, y: 2, z: 3 };
const defaultCameraPosition: [number, number, number] = [5, 5, 5];
const defaultCameraTarget: [number, number, number] = [0, 0, 0];

// Cache for guest points to avoid recalculation
let guestPointsCache: { key: string; points: GuestPoint[] } | null = null;

// Cache for dimension ranges to avoid repeated calculations
let dimensionRangeCache: Map<number, { min: number; max: number }> | null = null;
let dimensionRangeCacheKey: string | null = null;

// Helper to generate unique IDs
let sliceIdCounter = 0;
const generateSliceId = () => `slice-${++sliceIdCounter}`;

export const usePCAStore = create<PCAStore>((set, get) => ({
  // Initial state
  pcaData: null,
  loading: false,
  error: null,
  axisSelection: defaultAxisSelection,
  selectedGuest: null,
  hoveredGuest: null,
  comparisonGuest: null,
  showComparisonLine: false,
  comparisonMode: 'idle',
  comparisonFirstGuest: null,
  mode: 'all',
  viewMode: '3d',
  colorMode: 'cluster',  // Default to cluster coloring
  sizeMode: 'popularity',  // Default to popularity-based sizing
  selectedCluster: null, // No cluster selected by default
  cameraPosition: defaultCameraPosition,
  cameraTarget: defaultCameraTarget,
  slices: [],
  resetTrigger: 0,
  // Quiz state
  userCoordinates: null,
  userAnswers: {},
  isQuizActive: false,

  // Actions
  loadData: async (mode: DataMode) => {
    set({ loading: true, error: null });
    try {
      const data = await loadPCAData(mode);
      // Clear caches when new data loads
      guestPointsCache = null;
      dimensionRangeCache = null;
      dimensionRangeCacheKey = null;
      set({ pcaData: data, loading: false, mode: data.mode });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load data',
        loading: false 
      });
    }
  },

  setAxisSelection: (axis: Partial<AxisSelection>) => {
    get().clearCache();
    set(state => ({
      axisSelection: { ...state.axisSelection, ...axis }
    }));
  },

  setSelectedGuest: (guest: string | null) => {
    // When selecting a new guest directly (not through comparison flow), clear all comparison and cluster state
    set({ 
      selectedGuest: guest, 
      selectedCluster: null,
      comparisonGuest: null,
      comparisonFirstGuest: null,
      showComparisonLine: false,
      comparisonMode: 'idle'
    });
  },

  setHoveredGuest: (guest: string | null) => {
    set({ hoveredGuest: guest });
  },

  setComparisonGuest: (guest: string | null) => {
    // Clear comparison state when setting to null
    if (guest === null) {
      set({ 
        comparisonGuest: null, 
        showComparisonLine: false,
        comparisonMode: 'idle',
        comparisonFirstGuest: null
      });
    } else {
      set({ comparisonGuest: guest });
    }
  },

  // Used by "Find Most Different" - shows line, resets Compare PMs state
  setMostDifferentComparison: (guest: string | null) => {
    set({ 
      comparisonGuest: guest,
      showComparisonLine: guest !== null,
      comparisonMode: 'idle',
      comparisonFirstGuest: null // Clear Compare PMs state
    });
  },

  // Used by "Compare PMs" - shows line, keeps comparisonFirstGuest
  setCompareComparison: (firstGuest: string, secondGuest: string) => {
    set({
      comparisonFirstGuest: firstGuest,
      comparisonGuest: secondGuest,
      showComparisonLine: true,
      comparisonMode: 'idle',
      selectedGuest: firstGuest // Keep first PM selected in the space
    });
  },

  setComparisonMode: (mode: 'idle' | 'waiting-first' | 'waiting-second') => {
    set({ comparisonMode: mode });
    // If resetting to idle without completing comparison, clear first guest
    if (mode === 'idle') {
      const { comparisonGuest } = get();
      // Only clear first guest if there's no active comparison
      if (!comparisonGuest) {
        set({ comparisonFirstGuest: null });
      }
    }
  },

  setComparisonFirstGuest: (guest: string | null) => {
    set({ comparisonFirstGuest: guest });
  },

  setMode: async (mode: DataMode) => {
    get().clearCache();
    await get().loadData(mode);
  },

  setViewMode: (mode: '3d' | '2d') => {
    set({ viewMode: mode });
  },

  setColorMode: (mode: ColorMode) => {
    get().clearCache();
    set({ colorMode: mode });
  },

  setSizeMode: (mode: 'fixed' | 'popularity') => {
    get().clearCache();
    set({ sizeMode: mode });
  },

  setSelectedCluster: (cluster: number | null) => {
    // Clear individual guest selection when selecting a cluster
    if (cluster !== null) {
      set({ 
        selectedCluster: cluster, 
        selectedGuest: null,
        comparisonGuest: null,
        comparisonFirstGuest: null,
        showComparisonLine: false,
        comparisonMode: 'idle'
      });
    } else {
      set({ selectedCluster: null });
    }
  },

  setCameraPosition: (position: [number, number, number]) => {
    set({ cameraPosition: position });
  },

  setCameraTarget: (target: [number, number, number]) => {
    set({ cameraTarget: target });
  },

  resetCamera: () => {
    set({
      cameraPosition: defaultCameraPosition,
      cameraTarget: defaultCameraTarget
    });
  },

  resetView: () => {
    const current = get();
    set({
      axisSelection: defaultAxisSelection,
      cameraPosition: defaultCameraPosition,
      cameraTarget: defaultCameraTarget,
      selectedGuest: null,
      hoveredGuest: null,
      comparisonGuest: null,
      showComparisonLine: false,
      resetTrigger: (current.resetTrigger || 0) + 1 // Increment to trigger reset
    });
    get().clearCache();
  },

  // Quiz actions
  setQuizActive: (active: boolean) => {
    set({ isQuizActive: active });
  },

  setUserAnswer: (questionId: string, value: number) => {
    const current = get();
    set({
      userAnswers: {
        ...current.userAnswers,
        [questionId]: value
      }
    });
  },

  setUserCoordinates: (coordinates: number[]) => {
    set({ userCoordinates: coordinates });
    // Auto-select user's point
    set({ selectedGuest: '__USER__' });
  },

  clearUserData: () => {
    set({
      userCoordinates: null,
      userAnswers: {},
      isQuizActive: false,
      selectedGuest: null
    });
  },

  getGuestPoints: (): GuestPoint[] => {
    const { pcaData, axisSelection, slices, getDimensionRange, colorMode, sizeMode } = get();
    if (!pcaData) return [];

    // Create cache key including slices, color mode, and size mode
    const slicesKey = slices.map(s => `${s.id}:${s.dimension}:${s.position}:${s.width}:${s.enabled}`).join('|');
    const cacheKey = `${pcaData.mode}-${axisSelection.x}-${axisSelection.y}-${axisSelection.z}-${slicesKey}-${colorMode}-${sizeMode}`;
    
    // Return cached if available
    if (guestPointsCache && guestPointsCache.key === cacheKey) {
      return guestPointsCache.points;
    }

    // Cache miss - recalculate (this is expensive but necessary)
    const minMax = calculateMinMax(pcaData.guestCoordinates, axisSelection);

    // Pre-calculate all dimension ranges (cached, so fast)
    const activeSlices = slices.filter(s => s.enabled);
    const dimensionRanges = new Map<number, { min: number; max: number }>();
    for (const slice of activeSlices) {
      if (!dimensionRanges.has(slice.dimension)) {
        dimensionRanges.set(slice.dimension, getDimensionRange(slice.dimension));
      }
    }

    // Optimized visibility calculation
    const calculateVisibilityOptimized = (guest: GuestCoordinates): number => {
      if (activeSlices.length === 0) return 1;
      let minVisibility = 1;
      for (const slice of activeSlices) {
        const value = guest.coordinates[slice.dimension - 1] || 0;
        const range = dimensionRanges.get(slice.dimension)!;
        const rangeSize = range.max - range.min || 1;
        const distance = Math.abs(value - slice.position);
        const tolerance = slice.width * rangeSize;
        if (distance > tolerance) {
          const fadeRange = tolerance * 0.5;
          const fadeDistance = distance - tolerance;
          const visibility = Math.max(0, 1 - (fadeDistance / fadeRange));
          minVisibility = Math.min(minVisibility, visibility);
        }
      }
      return minVisibility;
    };

    // Calculate all points
    const points: GuestPoint[] = pcaData.guestCoordinates.map(guest => {
      const x = guest.coordinates[axisSelection.x - 1] || 0;
      const y = guest.coordinates[axisSelection.y - 1] || 0;
      const z = guest.coordinates[axisSelection.z - 1] || 0;
      const color = colorMode === 'cluster' 
        ? getColorForCluster(guest.cluster)
        : getColorForGuest(guest, axisSelection, minMax);
      
      // Calculate size based on mode
      const popularity = sizeMode === 'popularity' ? getGuestPopularity(guest.guest) : undefined;
      const size = getPointSize(undefined, popularity, sizeMode === 'popularity');
      
      const visibility = calculateVisibilityOptimized(guest);
      return {
        name: guest.guest,
        position: [x, y, z] as [number, number, number],
        color,
        size,
        visibility
      };
    });

    // Cache the result
    guestPointsCache = { key: cacheKey, points };
    return points;
  },

  clearCache: () => {
    guestPointsCache = null;
  },

  // Slice actions
  addSlice: (dimension: number) => {
    const { getDimensionRange, slices, axisSelection } = get();
    
    // Don't add if dimension is already being used as an axis or already has a slice
    if (dimension === axisSelection.x || dimension === axisSelection.y || dimension === axisSelection.z) {
      return;
    }
    if (slices.some(s => s.dimension === dimension)) {
      return;
    }

    const range = getDimensionRange(dimension);
    const midpoint = (range.min + range.max) / 2;

    get().clearCache();
    set(state => ({
      slices: [
        ...state.slices,
        {
          id: generateSliceId(),
          dimension,
          position: midpoint,
          width: 0.3, // Default to 30% of range
          enabled: true
        }
      ]
    }));
  },

  updateSlice: (id: string, config: Partial<Omit<SliceConfig, 'id'>>) => {
    const { getDimensionRange } = get();
    // Don't clear cache here - let cache key mismatch handle it naturally
    // This allows old cache to be returned while new calculation happens
    
    set(state => ({
      slices: state.slices.map(slice => {
        if (slice.id !== id) return slice;
        
        // If dimension is changing, reset position to midpoint of new dimension
        if (config.dimension && config.dimension !== slice.dimension) {
          const range = getDimensionRange(config.dimension);
          const midpoint = (range.min + range.max) / 2;
          return { ...slice, ...config, position: midpoint };
        }
        
        return { ...slice, ...config };
      })
    }));
  },

  removeSlice: (id: string) => {
    get().clearCache();
    set(state => ({
      slices: state.slices.filter(slice => slice.id !== id)
    }));
  },

  clearSlices: () => {
    get().clearCache();
    set({ slices: [] });
  },

  getDimensionRange: (dimension: number): { min: number; max: number } => {
    const { pcaData } = get();
    if (!pcaData) return { min: -1, max: 1 };

    // Check cache
    const cacheKey = `${pcaData.mode}-${pcaData.guestCoordinates.length}`;
    if (dimensionRangeCache && dimensionRangeCacheKey === cacheKey) {
      const cached = dimensionRangeCache.get(dimension);
      if (cached) return cached;
    } else {
      // Initialize cache
      dimensionRangeCache = new Map();
      dimensionRangeCacheKey = cacheKey;
    }

    // Calculate range
    const values = pcaData.guestCoordinates.map(g => g.coordinates[dimension - 1] || 0);
    const range = {
      min: Math.min(...values),
      max: Math.max(...values)
    };
    
    // Cache it
    dimensionRangeCache.set(dimension, range);
    return range;
  },

  getGuestByName: (name: string) => {
    const { pcaData } = get();
    if (!pcaData) return null;
    return pcaData.guestCoordinates.find(g => g.guest === name) || null;
  },

  getGuestCluster: (name: string) => {
    const { pcaData } = get();
    if (!pcaData) return undefined;
    const guest = pcaData.guestCoordinates.find(g => g.guest === name);
    return guest?.cluster;
  },

  getDimensionName: (dimension: number): string => {
    const { pcaData } = get();
    if (!pcaData) return `Dimension ${dimension}`;
    
    const dimKey = `Dimension_${dimension}`;
    const dimDef = pcaData.dimensionDefinitions[dimKey];
    
    if (dimDef?.axis_name) {
      return dimDef.axis_name;
    }
    return `Dimension ${dimension}`;
  },

  getDimensionLabels: (dimension: number): { low: string; high: string } => {
    const { pcaData } = get();
    if (!pcaData) return { low: 'Low', high: 'High' };
    
    const dimKey = `Dimension_${dimension}`;
    const dimDef = pcaData.dimensionDefinitions[dimKey];
    
    return {
      low: dimDef?.low_label || 'Low',
      high: dimDef?.high_label || 'High'
    };
  },

  getTotalDimensions: (): number => {
    const { pcaData } = get();
    if (!pcaData || pcaData.guestCoordinates.length === 0) return 30;
    return pcaData.guestCoordinates[0].coordinates.length;
  },

  getUniqueClusters: (): number[] => {
    const { pcaData } = get();
    if (!pcaData) return [];
    const clusters = pcaData.guestCoordinates
      .map(g => g.cluster)
      .filter((c): c is number => c !== undefined);
    return [...new Set(clusters)].sort((a, b) => a - b);
  },

  getUserPoint: (): GuestPoint | null => {
    const { userCoordinates, axisSelection } = get();
    if (!userCoordinates || userCoordinates.length !== 20) return null;

    // Project user's 20D coordinates to 3D using selected axes
    const x = userCoordinates[axisSelection.x - 1] || 0;
    const y = userCoordinates[axisSelection.y - 1] || 0;
    const z = userCoordinates[axisSelection.z - 1] || 0;

    // Fixed size for user point (slightly larger)
    const size = 0.2;

    return {
      name: '__USER__',
      position: [x, y, z],
      color: '#FF0000', // Strong red
      size,
      visibility: 1
    };
  },

  getDistanceBetween: (guest1: string, guest2: string): number | null => {
    const { pcaData, userCoordinates } = get();
    if (!pcaData) return null;

    // Handle __USER__ case
    const g1Coords = guest1 === '__USER__' 
      ? userCoordinates 
      : pcaData.guestCoordinates.find(g => g.guest === guest1)?.coordinates;
    const g2Coords = guest2 === '__USER__' 
      ? userCoordinates 
      : pcaData.guestCoordinates.find(g => g.guest === guest2)?.coordinates;

    if (!g1Coords || !g2Coords) return null;

    // Calculate Euclidean distance in full PCA space
    let sumSquared = 0;
    const minLength = Math.min(g1Coords.length, g2Coords.length);
    
    for (let i = 0; i < minLength; i++) {
      const diff = g1Coords[i] - g2Coords[i];
      sumSquared += diff * diff;
    }

    return Math.sqrt(sumSquared);
  },

  getMostDifferentGuest: (guest: string): { name: string; distance: number } | null => {
    const { pcaData, getDistanceBetween } = get();
    if (!pcaData) return null;

    const targetGuest = pcaData.guestCoordinates.find(g => g.guest === guest);
    if (!targetGuest) return null;

    let maxDistance = -1;
    let mostDifferent: { name: string; distance: number } | null = null;

    for (const otherGuest of pcaData.guestCoordinates) {
      if (otherGuest.guest === guest) continue;

      const distance = getDistanceBetween(guest, otherGuest.guest);
      if (distance !== null && distance > maxDistance) {
        maxDistance = distance;
        mostDifferent = { name: otherGuest.guest, distance };
      }
    }

    return mostDifferent;
  },

  getGuestDifferences: (guest1: string, guest2: string) => {
    const { pcaData, getDistanceBetween, getDimensionName, userCoordinates } = get();
    if (!pcaData) return null;

    // Handle __USER__ case
    const g1Coords = guest1 === '__USER__' 
      ? userCoordinates 
      : pcaData.guestCoordinates.find(g => g.guest === guest1)?.coordinates;
    const g2Coords = guest2 === '__USER__' 
      ? userCoordinates 
      : pcaData.guestCoordinates.find(g => g.guest === guest2)?.coordinates;

    if (!g1Coords || !g2Coords) return null;
    
    // Create fake guest objects for compatibility
    const g1 = { coordinates: g1Coords };
    const g2 = { coordinates: g2Coords };

    const distance = getDistanceBetween(guest1, guest2);
    if (distance === null) return null;

    // Calculate differences per dimension
    const differences: Array<{
      dimension: number;
      name: string;
      diff: number;
      guest1Value: number;
      guest2Value: number;
    }> = [];

    const minLength = Math.min(g1.coordinates.length, g2.coordinates.length);
    for (let i = 0; i < minLength; i++) {
      const dim = i + 1;
      const diff = Math.abs(g1.coordinates[i] - g2.coordinates[i]);
      differences.push({
        dimension: dim,
        name: getDimensionName(dim),
        diff,
        guest1Value: g1.coordinates[i],
        guest2Value: g2.coordinates[i],
      });
    }

    // Sort by difference and take top 5
    differences.sort((a, b) => b.diff - a.diff);
    const topDifferences = differences.slice(0, 5);

    return {
      distance,
      topDifferences,
    };
  },

  getAllGuestDifferences: (guest1: string, guest2: string) => {
    const { pcaData, getDistanceBetween, getDimensionName, userCoordinates } = get();
    if (!pcaData) return null;

    // Handle __USER__ case
    const g1Coords = guest1 === '__USER__' 
      ? userCoordinates 
      : pcaData.guestCoordinates.find(g => g.guest === guest1)?.coordinates;
    const g2Coords = guest2 === '__USER__' 
      ? userCoordinates 
      : pcaData.guestCoordinates.find(g => g.guest === guest2)?.coordinates;

    if (!g1Coords || !g2Coords) return null;
    
    // Create fake guest objects for compatibility
    const g1 = { coordinates: g1Coords };
    const g2 = { coordinates: g2Coords };

    const distance = getDistanceBetween(guest1, guest2);
    if (distance === null) return null;

    // Calculate absolute differences per dimension (all dimensions, not just top 5)
    const allDifferences: Array<{
      dimension: number;
      name: string;
      diff: number;
      guest1Value: number;
      guest2Value: number;
    }> = [];

    const minLength = Math.min(g1.coordinates.length, g2.coordinates.length);
    for (let i = 0; i < minLength; i++) {
      const dim = i + 1;
      const diff = Math.abs(g1.coordinates[i] - g2.coordinates[i]);
      allDifferences.push({
        dimension: dim,
        name: getDimensionName(dim),
        diff,
        guest1Value: g1.coordinates[i],
        guest2Value: g2.coordinates[i],
      });
    }

    // Sort by difference (largest first)
    allDifferences.sort((a, b) => b.diff - a.diff);

    return {
      distance,
      allDifferences,
    };
  },

  // Cache for all pairwise distances (computed once)
  getDistanceStats: () => {
    const { pcaData, getDistanceBetween } = get();
    if (!pcaData || pcaData.guestCoordinates.length < 2) return null;

    // Calculate all pairwise distances
    const distances: number[] = [];
    const guests = pcaData.guestCoordinates;
    
    for (let i = 0; i < guests.length; i++) {
      for (let j = i + 1; j < guests.length; j++) {
        const dist = getDistanceBetween(guests[i].guest, guests[j].guest);
        if (dist !== null) {
          distances.push(dist);
        }
      }
    }

    if (distances.length === 0) return null;

    // Sort for percentile calculations
    distances.sort((a, b) => a - b);
    
    const min = distances[0];
    const max = distances[distances.length - 1];
    const median = distances[Math.floor(distances.length / 2)];

    return { min, max, median, percentiles: distances };
  },

  // Get percentile score (0-100) for a given distance
  // Higher percentile = more different (farther apart)
  getDistancePercentile: (distance: number) => {
    const stats = get().getDistanceStats();
    if (!stats || stats.percentiles.length === 0) return 50;

    const { percentiles } = stats;
    
    // Binary search to find position in sorted distances
    let low = 0;
    let high = percentiles.length - 1;
    
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (percentiles[mid] < distance) {
        low = mid + 1;
      } else if (percentiles[mid] > distance) {
        high = mid - 1;
      } else {
        // Exact match
        return Math.round((mid / (percentiles.length - 1)) * 100);
      }
    }
    
    // low is the insertion point
    const percentile = (low / percentiles.length) * 100;
    return Math.round(Math.min(100, Math.max(0, percentile)));
  }
}));
